import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { usePostContentContext } from '@/postContentElement/context';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

import { useAnchorable } from '../../hooks/useAnchorable';

import { genericEmbed, genericEmbedChildrenContainer, genericEmbedContainer } from './GenericEmbed.css';
import type { TPostGenericEmbedProps } from './GenericEmbed.types';

export const PostGenericEmbed = ({
  element,
  children,
  editorMode,
  imageSlot,
  rewriteImageSrc,
  isIndexingDisabled,
}: TPostGenericEmbedProps) => {
  const { variant, url, target, imageUrl, imageWidthPercentage, imageVerticalAlign } = element.attrs;
  const { anchorAttributes } = useAnchorable(element.attrs);
  const { utmParams } = usePostContentContext();
  const urlWithUTMParams = evaluateAndAppendUTMParams(url, utmParams);

  const alteredImageUrl = imageUrl && rewriteImageSrc ? rewriteImageSrc(imageUrl) : imageUrl;
  const sanitizedImageWidthPercentage =
    typeof imageWidthPercentage === 'number' && Number.isFinite(imageWidthPercentage)
      ? Math.max(20, Math.min(60, Math.round(imageWidthPercentage)))
      : 40;

  const imageAlignSelf =
    imageVerticalAlign === 'top' ? 'flex-start' : imageVerticalAlign === 'bottom' ? 'flex-end' : 'center';

  const imageMarkup = imageSlot ?? <img src={alteredImageUrl} className="w-full h-auto object-cover" alt="" />;

  const content = (
    <div
      className={genericEmbed}
      style={
        {
          '--generic-embed-image-width': `${sanitizedImageWidthPercentage}%`,
        } as CSSProperties
      }
    >
      {variant === 'image-top-text-bottom' && (
        <div data-generic-embed-image-wrapper data-generic-embed-image-top className="pb-[12px]">
          {imageMarkup}
        </div>
      )}

      <div
        data-generic-embed-layout
        className={classNames(
          'border-collapse w-full flex sm:flex-row',
          variant === 'image-left-text-right' ? 'flex-col' : 'flex-col-reverse',
        )}
      >
        {variant === 'image-left-text-right' && (
          <div
            data-generic-embed-image-wrapper
            data-generic-embed-image-left
            className="w-full mb-[20px] sm:mb-0 sm:mr-[20px] sm:w-[var(--generic-embed-image-width)] sm:basis-[var(--generic-embed-image-width)] flex"
            style={{ alignSelf: imageAlignSelf }}
          >
            {imageMarkup}
          </div>
        )}

        <div className={genericEmbedChildrenContainer}>{children}</div>

        {variant === 'text-left-image-right' && (
          <div
            data-generic-embed-image-wrapper
            data-generic-embed-image-right
            className="w-full mb-[20px] sm:mb-0 sm:ml-[20px] sm:w-[var(--generic-embed-image-width)] sm:basis-[var(--generic-embed-image-width)] flex"
            style={{ alignSelf: imageAlignSelf }}
          >
            {imageMarkup}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div {...anchorAttributes} data-generic-embed-container className={genericEmbedContainer}>
      {editorMode ? (
        content
      ) : (
        <a
          href={urlWithUTMParams}
          target={target === '_self' ? undefined : '_blank'}
          rel={isIndexingDisabled ? 'noreferrer' : undefined}
        >
          {content}
        </a>
      )}
    </div>
  );
};
